import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { salesApi } from '../../../api';
import { useCart } from '../../../contexts/CartContext';

// Fonction utilitaire pour sélectionner des éléments aléatoires d'un tableau
const getRandomItems = (array, count) => {
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

const AnimatedPromo = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [promoSlides, setPromoSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saleProducts, setSaleProducts] = useState([]);
  const { addToCart } = useCart();

  // Récupérer les produits en solde
  useEffect(() => {
    const fetchSaleProducts = async () => {
      try {
        const response = await salesApi.list();
        
        if (response.success && Array.isArray(response.data)) {
          // Sauvegarder les produits complets pour l'ajout au panier
          setSaleProducts(response.data);
          
          // Sélectionner 3 produits aléatoires
          const randomProducts = getRandomItems(response.data, 3);
          
          // Formater les produits pour le carrousel
          const formattedSlides = randomProducts.map((product, index) => {
            const discount = Math.round(
              ((product.sale?.originalPrice - product.sale?.discountedPrice) / 
              product.sale?.originalPrice) * 100
            );
            
            // Définir un dégradé en fonction de l'index
            const gradients = [
              'from-accent to-error',
              'from-error to-warning',
              'from-warning to-success'
            ];
            
            return {
              id: product.id,
              title: product.name.toUpperCase(),
              subtitle: product.brand || 'COLLECTION EXCLUSIVE',
              discount: `${discount}%`,
              originalPrice: `${product.sale?.originalPrice || product.price}TND`,
              newPrice: `${product.sale?.discountedPrice || product.price}TND`,
              image: product.urlImage || 'https://via.placeholder.com/400x400?text=Produit+non+disponible',
              gradient: gradients[index % gradients.length]
            };
          });
          
          setPromoSlides(formattedSlides);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des produits en solde:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSaleProducts();
  }, []);

  // Démarrer l'animation uniquement si on a des slides
  useEffect(() => {
    if (promoSlides.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % promoSlides.length);
      }, 4000);

      return () => clearInterval(interval);
    }
  }, [promoSlides]);

  const currentPromo = promoSlides?.[currentSlide] || {};
  
  // Afficher un chargement si nécessaire
  if (isLoading) {
    return (
      <div className="bg-surface/90 backdrop-blur-lg border border-border rounded-3xl p-8 flex items-center justify-center h-96">
        <div className="animate-pulse text-center">
          <Icon name="Loader" size={32} className="animate-spin mx-auto mb-4 text-accent" />
          <p className="text-foreground/70">Chargement des offres spéciales...</p>
        </div>
      </div>
    );
  }
  
  // Si pas de produits
  if (promoSlides.length === 0) {
    return (
      <div className="bg-surface/90 backdrop-blur-lg border border-border rounded-3xl p-8 flex items-center justify-center h-96">
        <div className="text-center">
          <Icon name="AlertCircle" size={32} className="mx-auto mb-4 text-warning" />
          <p className="text-foreground/70">Aucune offre spéciale disponible pour le moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden py-6 lg:py-12">
      {/* Fond avec effet de dégradé dynamique */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-surface/50 to-background">
        <motion.div
          className="absolute inset-0 opacity-20"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(0, 255, 195, 0.1), transparent 50%)',
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      {/* Conteneur principal */}
      <div className="relative max-w-7xl mx-auto px-3 lg:px-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            {/* Carte de produit avec design split moderne */}
            <div className="relative bg-gradient-to-br from-surface/80 to-surface/40 backdrop-blur-3xl rounded-3xl lg:rounded-[3rem] overflow-hidden border border-white/10 shadow-2xl">
              
              {/* Effet de lumière flottante */}
              <motion.div
                className={`absolute top-0 right-0 w-72 lg:w-96 h-72 lg:h-96 bg-gradient-to-br ${currentPromo?.gradient} opacity-20 blur-3xl rounded-full`}
                animate={{
                  x: [0, 50, 0],
                  y: [0, -30, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
              />

              <div className="relative grid lg:grid-cols-2 gap-0">
                {/* Section Image - Gauche */}
                <motion.div
                  className="relative h-64 lg:h-auto"
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <div className="relative h-full lg:h-full overflow-hidden">
                    {/* Image du produit */}
                    <motion.img
                      src={currentPromo?.image}
                      alt={currentPromo?.title}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.6 }}
                    />
                    
                    {/* Overlay gradient pour meilleure lisibilité */}
                    <div className={`absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-black/60 via-black/20 to-transparent`}></div>
                    
                    {/* Badge de réduction en position absolue */}
                    <motion.div
                      className="absolute top-4 right-4 lg:top-6 lg:right-6"
                      animate={{ 
                        rotate: [0, 5, -5, 0],
                        scale: [1, 1.05, 1],
                      }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      <div className={`relative bg-gradient-to-br ${currentPromo?.gradient} rounded-2xl lg:rounded-3xl px-4 py-3 lg:px-6 lg:py-4 shadow-2xl`}>
                        <motion.div
                          className="absolute inset-0 bg-white/20 rounded-2xl lg:rounded-3xl"
                          animate={{ opacity: [0, 0.5, 0] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                        <div className="relative text-center">
                          <div className="text-3xl lg:text-5xl font-black text-white leading-none">
                            -{currentPromo?.discount}
                          </div>
                          <div className="text-xs lg:text-sm font-bold text-white/90 uppercase tracking-wider mt-1">
                            Réduction
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Badge "Produit Tendance" en bas */}
                    <motion.div
                      className="absolute bottom-4 left-4 lg:bottom-6 lg:left-6 bg-black/60 backdrop-blur-md rounded-xl lg:rounded-2xl px-3 py-2 lg:px-4 lg:py-3 border border-white/20"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.8 }}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon name="TrendingUp" className="text-accent" size={18} />
                        <span className="text-white font-bold text-xs lg:text-sm">Produit Tendance</span>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Section Contenu - Droite */}
                <motion.div
                  className="relative p-6 lg:p-10 flex flex-col justify-between"
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  {/* En-tête avec badge */}
                  <div className="space-y-4 lg:space-y-6">
                    {/* Badge Offre Limitée */}
                    <motion.div
                      className="inline-flex items-center space-x-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-full px-4 py-2 shadow-lg"
                      animate={{
                        boxShadow: [
                          '0 0 20px rgba(239, 68, 68, 0.3)',
                          '0 0 30px rgba(239, 68, 68, 0.6)',
                          '0 0 20px rgba(239, 68, 68, 0.3)',
                        ],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <Icon name="Zap" size={16} className="text-white" />
                      </motion.div>
                      <span className="font-black text-white text-xs lg:text-sm uppercase">
                        ⚡ Offre Flash
                      </span>
                    </motion.div>

                    {/* Titre du produit */}
                    <div>
                      <motion.h3
                        className="text-sm lg:text-base font-semibold text-foreground/60 mb-2"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                      >
                        {currentPromo?.subtitle}
                      </motion.h3>
                      <motion.h2
                        className={`text-3xl lg:text-5xl font-black leading-tight bg-gradient-to-r ${currentPromo?.gradient} bg-clip-text text-transparent`}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ 
                          y: 0, 
                          opacity: 1,
                        }}
                        transition={{ delay: 0.5 }}
                        style={{ 
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {currentPromo?.title}
                      </motion.h2>
                    </div>

                    {/* Affichage du prix */}
                    <motion.div
                      className="flex items-end space-x-4"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.6, type: "spring" }}
                    >
                      <div>
                        <div className="text-xs lg:text-sm text-foreground/50 mb-1">Prix soldé</div>
                        <div className={`text-4xl lg:text-6xl font-black bg-gradient-to-r ${currentPromo?.gradient} bg-clip-text text-transparent`}>
                          {currentPromo?.newPrice}
                        </div>
                      </div>
                      <div className="pb-2">
                        <div className="text-lg lg:text-xl text-foreground/40 line-through">
                          {currentPromo?.originalPrice}
                        </div>
                      </div>
                    </motion.div>

                    {/* Économies */}
                    <motion.div
                      className={`inline-flex items-center space-x-2 bg-gradient-to-r ${currentPromo?.gradient} rounded-full px-4 py-2`}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      <Icon name="TrendingDown" size={16} className="text-white" />
                      <span className="text-white font-bold text-sm">
                        Vous économisez {currentPromo?.discount} !
                      </span>
                    </motion.div>
                  </div>

                  {/* Actions */}
                  <motion.div
                    className="space-y-3 lg:space-y-4 mt-6 lg:mt-8"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.8 }}
                  >
                    {/* Bouton Principal */}
                    <motion.button
                      className={`w-full bg-gradient-to-r ${currentPromo?.gradient} text-white font-black text-base lg:text-lg py-4 lg:py-5 px-6 rounded-2xl shadow-xl relative overflow-hidden group`}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const product = saleProducts.find(p => p.id === currentPromo.id);
                        if (product) {
                          addToCart({
                            ...product,
                            quantity: 1,
                            price: product.sale?.discountedPrice || product.price,
                            originalPrice: product.sale?.originalPrice || product.price
                          });
                        }
                      }}
                    >
                      <motion.div
                        className="absolute inset-0 bg-white/20"
                        animate={{ x: ['-100%', '100%'] }}
                        transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 0.5 }}
                      />
                      <div className="relative flex items-center justify-center space-x-2">
                        <Icon name="ShoppingCart" size={20} />
                        <span className="uppercase tracking-wide">Ajouter au panier</span>
                        <motion.div animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                          <Icon name="ArrowRight" size={20} />
                        </motion.div>
                      </div>
                    </motion.button>

                    {/* Bouton Secondaire */}
                    <Link to="/sale">
                      <motion.button
                        className="w-full bg-white/5 backdrop-blur-sm border-2 border-accent/40 hover:border-accent text-accent font-bold text-sm lg:text-base py-3 lg:py-4 px-6 rounded-2xl transition-all duration-300"
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <Icon name="Tag" size={18} />
                          <span className="uppercase tracking-wide">Toutes les promos</span>
                        </div>
                      </motion.button>
                    </Link>

                    {/* Badge Stock Limité */}
                    <motion.div
                      className="flex items-center justify-center space-x-2 bg-warning/10 border border-warning/30 rounded-xl px-4 py-3"
                      animate={{ opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
                        <Icon name="Clock" size={16} className="text-warning" />
                      </motion.div>
                      <span className="text-warning font-semibold text-xs lg:text-sm">
                        ⏰ Stock limité - Commandez maintenant
                      </span>
                    </motion.div>
                  </motion.div>
                </motion.div>
              </div>
            </div>

            {/* Indicateurs de slides */}
            <motion.div
              className="flex items-center justify-center space-x-3 mt-6 lg:mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <button
                onClick={() => setCurrentSlide((currentSlide - 1 + promoSlides.length) % promoSlides.length)}
                className="p-2 lg:p-3 bg-surface/80 hover:bg-surface rounded-full border border-border transition-all"
              >
                <Icon name="ChevronLeft" size={20} className="text-foreground" />
              </button>

              <div className="flex items-center space-x-2">
                {promoSlides?.map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`transition-all duration-300 ${
                      index === currentSlide
                        ? 'w-8 lg:w-12 h-2 lg:h-3'
                        : 'w-2 lg:w-3 h-2 lg:h-3'
                    } rounded-full overflow-hidden`}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <div
                      className={`w-full h-full ${
                        index === currentSlide
                          ? `bg-gradient-to-r ${currentPromo?.gradient}`
                          : 'bg-foreground/20'
                      }`}
                    />
                  </motion.button>
                ))}
              </div>

              <button
                onClick={() => setCurrentSlide((currentSlide + 1) % promoSlides.length)}
                className="p-2 lg:p-3 bg-surface/80 hover:bg-surface rounded-full border border-border transition-all"
              >
                <Icon name="ChevronRight" size={20} className="text-foreground" />
              </button>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Particules décoratives (visibles uniquement sur desktop) */}
      <div className="hidden lg:block absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-1 h-1 rounded-full bg-gradient-to-r ${currentPromo?.gradient}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -40, 0],
              opacity: [0, 0.6, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default AnimatedPromo;